/**
 * Sanitize a URL for error reporting: drop query/hash so tokens never leave the client.
 */
export const sanitizeUrlForTracking = (url?: string | null): string | undefined => {
  if (!url) return undefined;
  try {
    if (url.startsWith('http://') || url.startsWith('https://')) {
      const parsed = new URL(url);
      return `${parsed.origin}${parsed.pathname}`;
    }
    const pathOnly = url.split('?')[0]?.split('#')[0];
    return pathOnly || undefined;
  } catch {
    return url.split('?')[0]?.split('#')[0] || undefined;
  }
};

const ID_PATH_SEGMENT_RE = /^(\d+|[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12})$/i;

/**
 * Sanitized URL with entity ids collapsed to `:id`, so one endpoint stays one issue
 * instead of a new group per ticket / article / chat.
 */
export const normalizeUrlForTracking = (url?: string | null): string | undefined => {
  const sanitized = sanitizeUrlForTracking(url);
  if (!sanitized) return undefined;
  return sanitized
    .split('/')
    .map((segment) => (ID_PATH_SEGMENT_RE.test(segment) ? ':id' : segment))
    .join('/');
};

/**
 * Paths / substrings that poll frequently — do not report to GlitchTip.
 */
const POLLING_URL_BLACKLIST: readonly string[] = [
  'endUserStatus',
  'end-user-status',
  'end_user_status',
  'userStatus',
  'heartbeat',
  '/presence',
  'aiChatEndUserStatus',
];

export const isPollingUrl = (url?: string | null): boolean => {
  if (!url) return false;
  const normalized = url.toLowerCase();
  return POLLING_URL_BLACKLIST.some((fragment) => normalized.includes(fragment.toLowerCase()));
};
